/**
 * Service for handling audio playback
 */
class AudioService {
  private audio: HTMLAudioElement | null = null;

  /**
   * Play audio from base64 data
   * @param audioBase64 Audio data as base64 string (data URL format)
   * @returns Promise that resolves when audio playback is complete
   */
  async playAudio(audioBase64: string): Promise<void> {
    try {
      // Stop any currently playing audio
      await this.stopAudio();

      // Validate audio data
      if (!audioBase64 || !audioBase64.startsWith('data:audio/')) {
        throw new Error('Invalid audio data format');
      }

      // Create and play audio directly from base64 data URL
      this.audio = new Audio();
      this.audio.preload = 'auto';
      this.audio.src = audioBase64;

      // Return promise that resolves when audio playback is complete
      return new Promise((resolve, reject) => {
        if (!this.audio) {
          reject(new Error('Audio element not created'));
          return;
        }

        // Set up event handlers
        this.audio.onended = () => {
          resolve();
        };

        this.audio.onerror = (event) => {
          const error = this.audio?.error;
          console.error('Audio playback error:', error?.code, error?.message, event);
          reject(new Error(`Audio playback failed: ${error?.message || 'Unknown error'}`));
        };

        // Wait for audio to be loaded before playing
        this.audio.oncanplaythrough = () => {
          console.log('Audio loaded and ready to play');
        };

        // Load and play the audio
        this.audio.load();

        // Attempt to play after a brief moment to ensure loading starts
        setTimeout(() => {
          if (this.audio) {
            console.log('Attempting to play audio...');
            this.audio.play().catch((error) => {
              console.error('Error calling play():', error);
              reject(new Error(`Failed to play audio: ${error.message || 'Unknown error'}`));
            });
          }
        }, 100);
      });
    } catch (error) {
      console.error('Error during audio playback:', error);
      throw error;
    }
  }

  /**
   * Stop audio playback
   * @returns Promise that resolves when audio is stopped
   */
  async stopAudio(): Promise<void> {
    try {
      if (this.audio && !this.audio.paused) {
        this.audio.pause();
        this.audio.currentTime = 0;
      }
    } catch (error) {
      console.error('Error stopping audio playback:', error);
      throw error;
    }
  }

  /**
   * Set playback speed
   * @param speed Playback speed (e.g., 1.0 for normal speed)
   */
  setPlaybackSpeed(speed: number): void {
    try {
      if (this.audio) {
        this.audio.playbackRate = speed;
      }
    } catch (error) {
      console.error('Error setting playback speed:', error);
      throw error;
    }
  }

  /**
   * Check if audio is currently playing
   * @returns Boolean indicating if audio is playing
   */
  isPlaying(): boolean {
    return !!(this.audio && !this.audio.paused);
  }

  /**
   * Get current audio element
   * @returns Current HTMLAudioElement or null
   */
  getAudioElement(): HTMLAudioElement | null {
    return this.audio;
  }

  /**
   * Dispose of audio resources
   */
  dispose(): void {
    if (this.audio) {
      this.audio.pause();
      this.audio.src = '';
      this.audio = null;
    }
  }
}

export default new AudioService();
